import styled from "@emotion/styled";
// @mui
import {
  Box,
  Stack,
  Button,
  Tooltip,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  ButtonGroup,
  Switch,
  FormControlLabel,
  FormGroup
} from "@mui/material";
// utils
import { fDateMonthYear, fDateMonthDayYear } from "../utils/formatTime";
import { ICalendarViewOption, IViewType } from "../types/calendar";
import CalendarFilters from "./CalendarFilters";
import { useCalendar } from "../contexts/CalendarContext";
import { logEvent } from "../utils/analyticsLogger";
import useResponsive from "../../minimals/hooks/useResponsive";
import useLocalStorage from "../../minimals/hooks/useLocalStorage";
import Iconify from "../../minimals/components/iconify";

// ----------------------------------------------------------------------
export const VIEW_OPTIONS = [
  {
    id: "dayGridMonth",
    value: "dayGridMonth",
    label: "Month",
    icon: "ic:round-view-module",
    isExpanded: false
  },
  {
    id: "dayGridMonthExpanded",
    value: "dayGridMonthExpanded",
    label: "Expanded",
    icon: "ic:round-view-week",
    isExpanded: true
  },
  {
    id: "listWeekOneDay",
    value: "listWeekOneDay",
    label: "Day",
    icon: "ic:round-view-day",
    isExpanded: false
  },
  {
    id: "listWeek",
    value: "listWeek",
    label: "Agenda",
    icon: "ic:round-view-agenda",
    isExpanded: false
  }
] as ICalendarViewOption[];

// What the events are grouped/labelled by. Lives here (not in the page) so the
// toolbar and the page's mobile menu share one source without a circular import.
export const VIEW_MODE_OPTIONS: { value: IViewType; label: string }[] = [
  { value: "client", label: "Client" },
  { value: "rep", label: "Assigned To" },
  { value: "titlecloser", label: "Title Closer" }
];
// ----------------------------------------------------------------------

type Props = {
  date: Date;
  view: string;
  onToday: VoidFunction;
  onNextDate: VoidFunction;
  onPrevDate: VoidFunction;
  onGoToDate: (date: string) => void;
  onChangeView: (newView: ICalendarViewOption) => void;
  isDisplayWeekends: boolean;
  handleToggleDisplayWeekends: (isChecked: boolean) => void;
  onOpenFilters: VoidFunction;
};

export default function CalendarToolbar({
  date,
  view,
  onToday,
  onNextDate,
  onPrevDate,
  onChangeView,
  onGoToDate,
  isDisplayWeekends,
  handleToggleDisplayWeekends,
  onOpenFilters
}: Props) {
  const { viewMode, setViewMode } = useCalendar();

  const isDesktop = useResponsive("up", "sm");
  // Mobile moves the filters out of the inline collapse and into a side drawer.
  const isMobile = useResponsive("down", "md");
  const [isOpenFilters, setIsOpenFilters] = useLocalStorage("isOpenFilters", true);

  const setViewModeAndLog = (mode: IViewType) => {
    logEvent("Closing Calendar", "Filter 2", mode);
    setViewMode(mode);
  };

  const dateNavigation = (
    <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }}>
      <IconButton size={isMobile ? "small" : "medium"} onClick={onPrevDate}>
        <Iconify icon="eva:arrow-ios-back-fill" />
      </IconButton>

      <Typography variant="h5" sx={{ fontSize: { xs: 16, sm: undefined }, whiteSpace: { xs: "nowrap", sm: "normal" } }}>
        {view === "dayGridMonth" || view === "dayGridMonthExpanded" ? fDateMonthYear(date) : fDateMonthDayYear(date)}
      </Typography>

      <IconButton size={isMobile ? "small" : "medium"} onClick={onNextDate}>
        <Iconify icon="eva:arrow-ios-forward-fill" />
      </IconButton>
    </Stack>
  );
  return (
    <ToolbarWrap>
      {/* Mobile's filter drawer lives on the page so the floating bar can open it too. */}
      {!isMobile && (
        <Collapse in={isOpenFilters} timeout="auto" unmountOnExit>
          <CalendarFilters onGoToDate={onGoToDate} />
        </Collapse>
      )}
      <Stack
        alignItems="center"
        justifyContent="space-between"
        direction={{ xs: "column", sm: "row" }}
        sx={{ px: { xs: 0, sm: 2.5 }, py: { xs: 1.25, sm: 2.5 } }}
      >
        {isDesktop && (
          <Stack direction="row" spacing={1}>
            <ToggleButtonGroup value={view} exclusive aria-label="calendar-view">
              {VIEW_OPTIONS.map((viewOption) => (
                <Tooltip key={viewOption.id} title={viewOption.label}>
                  <ToggleButton
                    size="small"
                    value={viewOption.id}
                    selected={viewOption.id === view}
                    onChange={() => onChangeView(viewOption)}
                  >
                    <Iconify icon={viewOption.icon} />
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Stack>
        )}
        {/* Mobile renders this in the site header, beside New Closing. */}
        {!isMobile && (
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isDisplayWeekends}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleToggleDisplayWeekends(event.target.checked)
                  }
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label="Show Weekends"
            />
          </FormGroup>
        )}

        {isMobile ? (
          // Grid with equal 1fr sides: the date group centres against the row, not
          // against the space the chip leaves, so it holds still as the label
          // changes width between "Client" and "Title Closer".
          <Box
            sx={{
              width: 1,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 1
            }}
          >
            <Stack direction="row" justifyContent="flex-start" sx={{ minWidth: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isDisplayWeekends}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleToggleDisplayWeekends(event.target.checked)
                    }
                    inputProps={{ "aria-label": "Show weekends" }}
                  />
                }
                label="Weekends"
                sx={{
                  mr: 0,
                  ml: 0,
                  "& .MuiFormControlLabel-label": { fontSize: 11, whiteSpace: "nowrap" }
                }}
              />
            </Stack>
            {dateNavigation}
            {/* Desktop has a Today button in this row; mobile only had it buried in
                the floating view menu. The empty right cell is the natural home. */}
            <Stack direction="row" justifyContent="flex-end" sx={{ minWidth: 0 }}>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={onToday}
                sx={{ minWidth: 0, px: 1, fontSize: 12 }}
              >
                Today
              </Button>
            </Stack>
          </Box>
        ) : (
          dateNavigation
        )}

        {/* After this was done I found MUI has this built in anyone wants to change it. https://mui.com/material-ui/react-toggle-button/  */}
        {/* Mobile reaches these from the floating bar's menu instead. */}
        {!isMobile && (
          <ButtonGroup size="small">
            <Button
              onClick={() => setViewModeAndLog("client")}
              variant={viewMode == "client" ? "contained" : "outlined"}
            >
              Client
            </Button>
            <Button onClick={() => setViewModeAndLog("rep")} variant={viewMode == "rep" ? "contained" : "outlined"}>
              Assigned To
            </Button>
            <Button
              onClick={() => setViewModeAndLog("titlecloser")}
              variant={viewMode == "titlecloser" ? "contained" : "outlined"}
            >
              Title Closer
            </Button>
          </ButtonGroup>
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          {isDesktop && (
            <Button size="small" color="error" variant="contained" onClick={onToday}>
              Today
            </Button>
          )}

          {/* Desktop only — mobile reaches the filter drawer from the floating bar. */}
          {!isMobile && (
            <IconButton aria-label="Filters" onClick={() => setIsOpenFilters(!isOpenFilters)}>
              <Iconify icon="ic:round-filter-list" />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </ToolbarWrap>
  );
}

const ToolbarWrap = styled.div`
  background-color: #fff;
`;
